import { useState } from "react";
import TextBox from "../Primitives/TextBox";
import { IconButton } from "../Primitives/Buttons";
import AddIcon from "../../../assets/svg/add.svg";
import EditIcon from "../../../assets/svg/edit.svg";
import TrashIcon from "../../../assets/svg/trash.svg";
import folders from "../../stories/helpers/files/folders";
import FolderIcon from "../../../assets/svg/files/folder.svg";
import { Radio, Checkbox, CounterBadge } from "@fluentui/react-components";
import { ChevronDown20Filled, ChevronRight20Filled } from "@fluentui/react-icons";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { TFolder, buildAsTree, drawTreeAsString } from "../../models/folder-model";

export interface IFolderSelectedFileCount {
  [key: string]: number;
}

export type TFolderSelectMode = "SINGLE" | "MULTIPLE" | "NONE";

export interface TProps {
  matterId: string;
  folders: TFolder[];
  allowRename?: boolean;
  allowCreate?: boolean;
  allowDelete?: boolean;
  selectMode: TFolderSelectMode;
  folderAndSelectedFileCounts?: IFolderSelectedFileCount;

  onDelete: (folderId: string) => void;
  onSelectFolder: (id: string) => void;
  onCreate: (parentId: string, folderName: string) => Promise<string>;
  onRenameFolder: (folderId: string, oldName: string, newName: string) => void;
}

export const FolderExplorerPresentational = (props: TProps) => {
  const [newName, setNewName] = useState<string>("");
  const [openFolders, setOpenFolders] = useState<string[]>([]);
  const [checkedFolders, setCheckedFolders] = useState<string[]>([]);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setOpenFolders((prev) => (prev.includes(id) ? prev.filter((folderId) => folderId !== id) : [...prev, id]));
  };

  const handleMultiCheckChanged = (id: string) => {
    if (props.selectMode !== "MULTIPLE") {
      return;
    }

    let newCheckedFolders: string[];

    if (checkedFolders.includes(id)) {
      newCheckedFolders = checkedFolders.filter((folderId) => folderId !== id);
    } else {
      newCheckedFolders = [...checkedFolders, id];
    }

    setCheckedFolders(newCheckedFolders);
  };

  const handleSelect = (id: string) => {
    if (props.selectMode !== "MULTIPLE") {
      setCheckedFolders([id]);
    }

    if (editingFolder !== null) {
      exitRenameEditMode();
    }

    props.onSelectFolder && props.onSelectFolder(id);
  };

  const confirmFolderRename = (folderId: string, oldName: string, newName: string) => {
    console.info(`Confimring rename of folder: ${folderId}. Old name: ${oldName}. New name: ${newName}.`);

    if (props.onRenameFolder) {
      props.onRenameFolder(folderId, oldName, newName);
    }

    exitRenameEditMode();
  };

  const handleCreate = async (parentId: string) => {
    const folderName = prompt("Enter new folder name");

    if (folderName) {
      const newFolderId = await props.onCreate(parentId, folderName);
      if (newFolderId) {
        setOpenFolders((prev) => [...prev, parentId]);
      }
    }
  };

  const enterRenameEditMode = (id: string) => {
    const folder = props.folders.find((folder) => folder.id === id);
    if (folder) {
      console.info(`Entering folder rename edit mode for folder id: ${id}. Current name: ${folder.name}.`);

      setEditingFolder(id);
      setNewName(folder.name || "Documents");
    }
  };

  const exitRenameEditMode = () => {
    console.info(`Exiting folder rename edit mode for folder id: ${editingFolder}`);

    setNewName("");
    setEditingFolder(null);
  };

  const handleDoubleClick = (id: string, allowRename: boolean) => {
    if (!allowRename || editingFolder !== id) {
      exitRenameEditMode();
      return;
    }
    enterRenameEditMode(id);
  };

  const handleKeyPress = (
    id: string,
    allowRename: boolean,
    event: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!allowRename) {
      return;
    }

    if (event.key === "F2") {
      enterRenameEditMode(id);
      return;
    }

    if (event.key === "Escape") {
      exitRenameEditMode();
      return;
    }

    if (event.key === "Enter") {
      // Prevent default behavior of Enter key (form submission)
      event.preventDefault();

      if (editingFolder === null) {
        exitRenameEditMode();
        return;
      }

      const folder = folders.find((f) => f.id === editingFolder);

      if (folder) {
        confirmFolderRename(editingFolder, folder.name ?? "", newName);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
  };

  const renderTree = (node: TFolder) => {
    const isOpen = openFolders.includes(node.id);

    const isChecked = checkedFolders.includes(node.id);
    const indentStyle = { marginLeft: `${node.depth * 16}px` };
    const toggleIcon = isOpen ? <ChevronDown20Filled /> : <ChevronRight20Filled />;
    const selectedItemCount = (props.folderAndSelectedFileCounts && props.folderAndSelectedFileCounts[node.id]) ?? 0;

    // Note: Root folder is not "real". We can't rename or delete this one.
    const isRootFolder = !node.id || node.id === "";

    const allowCreate = props.allowCreate === true;
    const allowRename = !isRootFolder && props.allowRename === true;
    const allowDelete = !isRootFolder && props.allowDelete === true;
    const isFolderInEditMode = !isRootFolder && editingFolder === node.id;

    const selectedClass = isChecked
      ? "bg-blue-100 border-l-4 border-blue-600"
      : "bg-transparent border-l-4 border-transparent";

    return (
      <>
        <li className={`min-h-11 flex items-center group ${selectedClass}`}>
          <div style={indentStyle} className="flex items-center flex-1 min-w-0">
            <div
              className="flex items-center flex-1 mr-2"
              onClick={() => handleToggle(node.id)}
              onKeyDown={(e) => handleKeyPress(node.id, allowRename, e)}
              onDoubleClick={() => handleDoubleClick(node.id, allowRename)}
              tabIndex={0}
            >
              {toggleIcon}
              {props.selectMode === "SINGLE" ? (
                <Radio radioGroup="folderTree" checked={isChecked} />
              ) : props.selectMode === "MULTIPLE" ? (
                <Checkbox checked={isChecked} onChange={() => handleMultiCheckChanged(node.id)} />
              ) : (
                <></>
              )}
              <img
                className="min-w-[1rem] min-h-[1rem] w-7 h-7 text-descriptive-yellow-550"
                src={FolderIcon}
                alt="Folder icon"
              />
              {isFolderInEditMode ? (
                <TextBox type="text" value={newName} onChange={handleInputChange} autoFocus className="mx-2" />
              ) : (
                <span onClick={() => handleSelect(node.id)} className="cursor-pointer truncate">
                  {node.name}
                </span>
              )}
            </div>
            {isChecked && (allowRename || allowDelete || allowCreate) && (
              <div className="flex items-center">
                {allowRename && (
                  <IconButton
                    className="mr-2"
                    leftIcon={EditIcon}
                    onClick={() => handleDoubleClick(node.id, allowRename)}
                  />
                )}
                {allowDelete && (
                  <IconButton className="mr-2" leftIcon={TrashIcon} onClick={() => props.onDelete(node.id)} />
                )}
                {allowCreate && (
                  <IconButton className="mr-2" leftIcon={AddIcon} onClick={() => handleCreate(node.id)} />
                )}
              </div>
            )}
            {selectedItemCount > 0 && (
              <div className="flex items-center mr-2">
                <CounterBadge count={selectedItemCount} />
              </div>
            )}
          </div>
        </li>
        {isOpen && node.subFolders && node.subFolders.length > 0 && (
          <ul>{node.subFolders.map((subFolder) => renderTree(subFolder))}</ul>
        )}
      </>
    );
  };

  const rootNode = buildAsTree(props.matterId, props.folders);

  return <ul className="overflow-auto">{renderTree(rootNode)}</ul>;
};
